import { ColumnSourceEnum, PropertyBrowserNodeOUT } from "api-client";
import { PRODUCT_DEFAULTS_GROUP_ID } from "features/PropertyBrowser/propertyBrowserConsts";
import PropertyInput from "./PropertyInput";
import { ConditionProps } from "./ConditionRow";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  PROPERTY_RULE_OPERATORS,
  PROPERTY_TEMPLATE_OPERATORS,
} from "features/DynamicTable/consts";
import React from "react";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";
import PropertyBrowserTree from "features/PropertyBrowser/PropertyBrowserTree";

interface PropertyFieldProps {
  row: ConditionProps;
  onSelectValue: (value: PropertyBrowserNodeOUT | string) => void;
  onSelectOperator: (
    value: string | undefined | number,
    update?: boolean
  ) => void;
  onRemoveValue: () => void;
}

function ValuePropertyField(props: PropertyFieldProps) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const row = props.row;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [valueSearchMode, setValueSearchMode] = React.useState(false);

  React.useEffect(() => {
    setValueSearchMode(false);
  }, [rulesSetStore.currentRule?.id]);

  const isTypeNumber = () => row.operator?.includes("THAN");
  const isCurrentSourceTemplate =
    row.value_source === ColumnSourceEnum.TemplateVariable;

  const hideValueInput = () => row.operator?.includes("EMPTY");

  const handleChangeValue = (value: string) => {
    if (row.valueProperty) return;
    isCurrentSourceTemplate &&
      props.onSelectOperator(PROPERTY_RULE_OPERATORS[0].id, false);
    props.onSelectValue(value);
    setValueSearchMode(false);
  };

  const handleSelectPropertyValue = (item: PropertyBrowserNodeOUT) => {
    const switchToTemplateSource =
      item.column_source === ColumnSourceEnum.TemplateVariable &&
      !isCurrentSourceTemplate;
    const switchToOtherSources =
      item.column_source !== ColumnSourceEnum.TemplateVariable &&
      isCurrentSourceTemplate;

    if (switchToTemplateSource) {
      props.onSelectOperator(PROPERTY_TEMPLATE_OPERATORS[0].id, false);
    } else if (switchToOtherSources) {
      props.onSelectOperator(PROPERTY_RULE_OPERATORS[0].id, false);
    }
    props.onSelectValue(item);
    setValueSearchMode(false);
  };

  const handleRemoveValue = () => {
    props.onRemoveValue();
  };

  const getValue = () => {
    if (row.valueProperty) {
      return isEng ? row.valueProperty.name : row.valueProperty.name_de ?? "";
    }
    return row.value ?? "";
  };

  return (
    <div className="relative w-full min-w-[100px]">
      {hideValueInput() ? null : (
        <PropertyBrowserTree
          containerClassName="relative"
          optionsClassName="!max-w-fit min-w-[100px]"
          disableOpen={isTypeNumber()}
          rootGroupId={PRODUCT_DEFAULTS_GROUP_ID}
          showSearch={false}
          isOpen={(state) => {
            setValueSearchMode(state);
          }}
          onSelect={handleSelectPropertyValue}
          titleOrIcon={
            <PropertyInput
              value={getValue()}
              placeholder={t("rulesEditor.selectPropertyOrValue")}
              onRemoveClicked={handleRemoveValue}
              onChangeSearch={() => undefined}
              onChangeValue={handleChangeValue}
              isObject={!!row.valueProperty && !isCurrentSourceTemplate}
              isProduct={isCurrentSourceTemplate}
              hasError={
                !row.value &&
                !row.valueProperty &&
                !rulesSetStore.inProgressRuleID
              }
              type={isTypeNumber() ? "number" : "text"}
              searchMode={valueSearchMode}
              inputRef={inputRef}
              inputClassName={clsx({
                "pointer-events-none": row.valueProperty,
              })}
            />
          }
        />
      )}
    </div>
  );
}

export default observer(ValuePropertyField);
